import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy } from '@angular/core';
import { Observable, Subject, catchError, concatMap, delay, finalize, of } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { ActiveModal } from '@rocketfinancialcorp/rocket-ui/modal';

import { LoadingScreenComponent } from '@shared/components';
import { FinancialAccountList } from '@shared/models';
import { FlinksWebhookResponse } from '@shared/models/aggregation';
import { SafePipe } from '@shared/pipes';
import { FinancialAccountService } from '@shared/services';
import { ErrorUtils } from '@shared/utils';

@Component({
  selector: 'app-financial-account-verification-iframe',
  imports: [SafePipe, RktIconComponent, LoadingScreenComponent],
  templateUrl: './financial-account-verification-iframe.component.html',
  styleUrl: './financial-account-verification-iframe.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class FinancialAccountVerificationIframeComponent implements OnDestroy {
  connectionURL!: string;
  loadingMessage!: string;
  loading = false;
  accountSelected = false;
  accountHolderType!: 'CUSTOMER' | 'COUNTERPARTY' | 'BUSINESS_ACCOUNT' | undefined;
  operationType!: 'LINK' | 'VERIFY';
  entityId!: string;
  entityElementCount!: number;
  retryCount = 7;
  currentAttempt = 0;
  private stopPolling$ = new Subject<void>();

  constructor(
    public activeModal: ActiveModal,
    private elementRef: ElementRef,
    private financialAccountService: FinancialAccountService,
  ) {
    window.addEventListener('message', (evt: FlinksWebhookResponse) => this.outputMessage(evt));
  }

  ngOnDestroy() {
    this.stopPolling$.next();
    this.stopPolling$.complete();
  }

  modelInitData(data: {
    connectionURL: string;
    totalElements?: number;
    entityId?: string;
    accountHolderType?: 'CUSTOMER' | 'COUNTERPARTY' | 'BUSINESS_ACCOUNT';
    operationType: 'LINK' | 'VERIFY';
  }): void {
    const { connectionURL, totalElements, entityId, accountHolderType, operationType } = data;
    this.loading = true;
    this.loadingMessage = 'Setting things up. Please wait...';
    this.entityElementCount = totalElements ?? 0;
    this.entityId = entityId ?? '';
    this.accountHolderType = accountHolderType ?? undefined;
    this.operationType = operationType;
    this.initializeIframe(connectionURL);
  }

  initializeIframe(connectionURL: string) {
    this.connectionURL = connectionURL + '&headerEnable=false';
    const iframeElement = this.elementRef.nativeElement.querySelector('#iframe');
    const iframe = iframeElement.querySelector('iframe');
    if (iframe) {
      iframe.style.width = '100%';
      iframe.style.height = '100%';
    }
  }

  outputMessage(listenerMessage: FlinksWebhookResponse) {
    const { step } = listenerMessage.data;

    if (step === 'APP_MOUNTED') {
      this.loading = false;
    }

    if (step === 'ACCOUNT_SELECTED') {
      this.loading = true;

      this.loadingMessage = 'Linking bank account. Please wait...';

      if (this.operationType === 'LINK') {
        switch (this.accountHolderType) {
          case 'COUNTERPARTY':
            this.verifyFinancialAccountLinked(this.fetchCounterpartyFinancialAccount());
            break;

          case 'CUSTOMER':
            this.verifyFinancialAccountLinked(this.fetchCustomerFinancialAccount());
            break;

          case 'BUSINESS_ACCOUNT':
            this.verifyFinancialAccountLinked(this.fetchBusinessAccountFinancialAccount());
            break;
        }
      }

      if (this.operationType === 'VERIFY') {
        this.accountSelected = true;
        this.loading = false;
      }
    }
  }

  verifyFinancialAccountLinked(financialAccountListObservable: Observable<FinancialAccountList>) {
    this.loading = true;
    this.currentAttempt = 0;

    this.pollWithExponentialBackoff(financialAccountListObservable)
      .pipe(
        takeUntil(this.stopPolling$),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: (financialAccountListResponse) => {
          if (financialAccountListResponse.totalElements !== this.entityElementCount) {
            this.accountSelected = true;
            this.endSubscription();
          } else if (this.currentAttempt >= this.retryCount && financialAccountListResponse.totalElements === this.entityElementCount) {
            this.accountSelected = false;
            this.activeModal.close(false);
            this.endSubscription();
          }
        },
        error: (error) => ErrorUtils.catchError(error),
      });
  }

  private pollWithExponentialBackoff(financialAccountListObservable: Observable<FinancialAccountList>): Observable<FinancialAccountList> {
    return of(null).pipe(
      concatMap(() => {
        const delayTime = this.currentAttempt === 0 ? 2000 : Math.min(2000 * Math.pow(2, this.currentAttempt - 1), 30000);
        return of(null).pipe(delay(delayTime));
      }),
      concatMap(() =>
        financialAccountListObservable.pipe(
          catchError((error) => {
            ErrorUtils.catchError(error);
            if (this.currentAttempt < this.retryCount) {
              return of({ items: [], totalElements: this.entityElementCount } as FinancialAccountList);
            }
            throw error;
          }),
        ),
      ),
      tap(() => this.currentAttempt++),
      concatMap((response) => {
        if (response.totalElements === this.entityElementCount && this.currentAttempt < this.retryCount) {
          return this.pollWithExponentialBackoff(financialAccountListObservable);
        }
        return of(response);
      }),
    );
  }

  endSubscription() {
    this.stopPolling$.next();
    this.stopPolling$.complete();
  }

  fetchCounterpartyFinancialAccount() {
    return this.financialAccountService
      .getCounterpartyFinancialAccounts({
        counterpartyId: this.entityId,
        page: 0,
        size: 10,
      })
      .pipe(
        takeUntil(this.stopPolling$),
        map((response) => response),
      );
  }

  fetchCustomerFinancialAccount() {
    return this.financialAccountService.getCustomerFinancialAccounts({ customerId: this.entityId, page: 0, size: 10 }).pipe(
      takeUntil(this.stopPolling$),
      map((response) => response),
    );
  }

  fetchBusinessAccountFinancialAccount() {
    return this.financialAccountService
      .getFinancialAccounts({
        page: 0,
        size: 10,
        state: ['ACTIVE', 'SUSPENDED', 'PENDING'],
        accountHolderTypes: ['BUSINESS_ACCOUNT'],
      })
      .pipe(
        takeUntil(this.stopPolling$),
        map((response) => response),
      );
  }
}
